import { FlashcardsService } from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { SessionCompleteSummary } from "@/components/Flashcards/SessionCompleteSummary"
import { StudySession } from "@/components/Flashcards/StudySession"
import type { StudySessionCard } from "@/components/Flashcards/StudySession"
import { PageShell, PageSkeleton } from "@/components/Layout"
import useAuth from "@/hooks/useAuth"
import {
  clearStudySession,
  loadStudySession,
  saveStudySession,
} from "@/hooks/useStudySessionStore"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { Button } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"

type CustomSearch = {
  deckIds?: string[]
  subjectIds?: string[]
  ratings?: number[]
  includeNew?: boolean
  shuffle?: boolean
}

export const Route = createFileRoute("/_layout/study/custom")({
  validateSearch: (search: Record<string, unknown>): CustomSearch => ({
    deckIds: Array.isArray(search.deckIds)
      ? (search.deckIds as string[])
      : undefined,
    subjectIds: Array.isArray(search.subjectIds)
      ? (search.subjectIds as string[])
      : undefined,
    ratings: Array.isArray(search.ratings)
      ? (search.ratings as number[]).map(Number)
      : undefined,
    includeNew: search.includeNew === true || search.includeNew === "true",
    shuffle: search.shuffle === true || search.shuffle === "true",
  }),
  component: StudyCustomPage,
})

function StudyCustomPage() {
  const navigate = useNavigate()
  const breadcrumbs = useRouteBreadcrumbs("study.index", [
    { label: "Custom drill" },
  ])
  const { deckIds, subjectIds, ratings, includeNew, shuffle } =
    Route.useSearch()
  const { user } = useAuth()
  const sessionSize = user?.study_session_size ?? 50

  const mode = "custom" as const
  const paramsKey = JSON.stringify({
    deckIds,
    subjectIds,
    ratings,
    includeNew,
    shuffle,
  })

  const { data, isLoading } = useQuery({
    queryKey: [
      "flashcards-study",
      deckIds ?? [],
      subjectIds ?? [],
      ratings ?? [],
      includeNew,
      shuffle,
      sessionSize,
    ],
    queryFn: () =>
      FlashcardsService.getStudyCards({
        deckId: deckIds ?? [],
        subjectId: subjectIds ?? [],
        ratings: ratings ?? [],
        includeNew: includeNew ?? false,
        shuffle: shuffle ?? false,
        limit: sessionSize,
      }),
  })

  const [done, setDone] = useState(false)
  const [xpEarned, setXpEarned] = useState(0)
  const [sessionCards, setSessionCards] = useState<StudySessionCard[] | null>(
    null,
  )
  const [resumeIndex, setResumeIndex] = useState(0)
  const [resumeXp, setResumeXp] = useState(0)

  useEffect(() => {
    if (!isLoading && sessionCards === null) {
      const restored = loadStudySession()
      const restoredParams =
        restored === null
          ? null
          : { ...restored.params, shuffle: restored.params.shuffle ?? false }
      const matchesSnapshot =
        restored !== null &&
        restored.mode === mode &&
        JSON.stringify(restoredParams) === paramsKey &&
        restored.cards.length > 0 &&
        restored.index < restored.cards.length

      if (matchesSnapshot && restored !== null) {
        setSessionCards(restored.cards)
        setResumeIndex(restored.index)
        setResumeXp(restored.xpEarned)
      } else {
        const orderedCards = data?.data ?? []
        setSessionCards(orderedCards)
        saveStudySession({
          mode,
          params: { deckIds, subjectIds, ratings, includeNew, shuffle },
          cards: orderedCards,
          index: 0,
          xpEarned: 0,
          streak: 0,
          savedAt: Date.now(),
        })
      }
    }
  }, [
    data?.data,
    isLoading,
    sessionCards,
    paramsKey,
    mode,
    subjectIds,
    deckIds,
    ratings,
    includeNew,
    shuffle,
  ])

  const handleProgress = (index: number, xp: number) => {
    saveStudySession({
      mode,
      params: { deckIds, subjectIds, ratings, includeNew, shuffle },
      cards: sessionCards ?? [],
      index,
      xpEarned: xp,
      streak: 0,
      savedAt: Date.now(),
    })
  }

  const handleContinue = () => {
    clearStudySession()
    setDone(false)
    setSessionCards(null)
    setResumeIndex(0)
    setResumeXp(0)
    setXpEarned(0)
  }

  if (isLoading) {
    return (
      <PageShell title="Custom drill" breadcrumbs={breadcrumbs}>
        <PageSkeleton variant="detail" />
      </PageShell>
    )
  }

  const cards = sessionCards ?? data?.data ?? []

  if (done) {
    // StudySession invalidates the flashcards-study query on completion, so
    // `data` already reflects the next batch matching the same filters — a
    // non-empty result here means there's more to study beyond this cap.
    const hasMoreCards = (data?.data.length ?? 0) > 0

    return (
      <PageShell title="Custom drill" breadcrumbs={breadcrumbs}>
        <SessionCompleteSummary
          heading="Custom drill complete!"
          cardsCompleted={cards.length}
          xpEarned={xpEarned}
          onBack={() => navigate({ to: "/study" })}
          secondaryAction={
            hasMoreCards
              ? { label: "Continue studying", onClick: handleContinue }
              : undefined
          }
        />
      </PageShell>
    )
  }

  if (cards.length === 0) {
    return (
      <PageShell title="Custom drill" breadcrumbs={breadcrumbs}>
        <AppEmptyState
          title="No cards match this filter"
          description="Try adjusting your subject or band filters."
          action={
            <Button onClick={() => navigate({ to: "/study" })}>
              Back to Study Hub
            </Button>
          }
        />
      </PageShell>
    )
  }

  return (
    <PageShell
      title="Custom drill"
      breadcrumbs={breadcrumbs}
      py={{ base: 5, md: 8 }}
      contentGap={5}
    >
      <StudySession
        cards={cards}
        modeLabel="Custom drill"
        initialIndex={resumeIndex}
        initialXp={resumeXp}
        onProgress={handleProgress}
        onComplete={(xp) => {
          clearStudySession()
          setDone(true)
          setXpEarned(xp)
        }}
      />
    </PageShell>
  )
}

export default StudyCustomPage
